import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { IcoZap, UngeneratedEmployee } from '../PayrollManagement';
import ProfilePictureHover from '../../../components/ProfilePictureHover';
import GeneratePayrollPanel from './GeneratePayrollPanel';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface Props {
  open: boolean;
  onClose: () => void;
  onGenerated: () => void;
  toast: React.RefObject<Toast | null>;
  /** Pre-fills and locks the panel to a single employee when opened from the "not generated" strip. */
  prefillEmployee?: UngeneratedEmployee;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main GeneratePayrollModal
// ─────────────────────────────────────────────────────────────────────────────
const GeneratePayrollModal: React.FC<Props> = ({ open, onClose, onGenerated, toast, prefillEmployee }) => {
  const [isClosing, setIsClosing] = useState(false);

  // ── Closing animation (same pattern as BulkEditStudentsModal) ─────────────
  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.generate-payroll-modal .p-dialog') as HTMLElement | null;
      const mask   = document.querySelector('.generate-payroll-modal')           as HTMLElement | null;
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask)   mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { onClose(); setIsClosing(false); }, 400);
  };

  // ── Header ────────────────────────────────────────────────────────────────
  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      {prefillEmployee ? (
        <ProfilePictureHover
          src={prefillEmployee.profile_picture ?? ''}
          name={prefillEmployee.employee_name ?? `#${prefillEmployee.employee_id}`}
          size={54}
          previewSize={220}
          borderRadius={14}
        />
      ) : (
        <div style={{
          width: 54, height: 54, borderRadius: 14,
          background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 22, flexShrink: 0,
          border: '3px solid white', boxShadow: '0 8px 24px rgba(59,130,246,.3)',
        }}>
          <IcoZap size={22} color="white" />
        </div>
      )}
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af', marginBottom: 4 }}>Generate Payroll</div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {prefillEmployee
            ? <>Scoped to <span style={{ color: '#1e40af', fontWeight: 700 }}>{prefillEmployee.employee_name ?? `#${prefillEmployee.employee_id}`}</span></>
            : 'Runs for all employees unless a specific one is chosen'}
        </div>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog
      visible={open && !isClosing}
      onHide={handleClose}
      header={headerTemplate}
      style={{ width: '98vw', maxWidth: 640, borderRadius: 24, overflow: 'hidden' }}
      contentStyle={{
        padding: 20,
        background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)',
        maxHeight: 'calc(92vh - 120px)',
        overflowY: 'auto',
      }}
      headerStyle={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '20px 24px',
        borderRadius: '24px 24px 0 0',
      }}
      modal
      dismissableMask
      draggable={false}
      resizable={false}
      blockScroll
      maskClassName="generate-payroll-modal"
    >
      <style>{`
        .generate-payroll-modal { backdrop-filter:blur(4px); background-color:rgba(0,0,0,.25)!important; animation:gpMaskFadeIn .35s ease; }
        .generate-payroll-modal.mask-closing { animation:gpMaskFadeOut .4s ease!important; }
        @keyframes gpMaskFadeIn { from{opacity:0} to{opacity:1} }
        @keyframes gpMaskFadeOut { from{opacity:1} to{opacity:0} }
        .generate-payroll-modal .p-dialog { animation:gpDialogOpen .35s cubic-bezier(.4,0,.2,1); box-shadow:0 25px 50px -12px rgba(0,0,0,.25)!important; }
        .generate-payroll-modal .p-dialog.dialog-closing { animation:gpDialogClose .4s cubic-bezier(.4,0,.2,1)!important; }
        @keyframes gpDialogOpen { from{opacity:0;transform:scale(.92) translateY(-20px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes gpDialogClose { from{opacity:1;transform:scale(1) translateY(0)} to{opacity:0;transform:scale(.88) translateY(25px)} }
        .gp-card .pr-field { display:flex; flex-direction:column; gap:6px; }
        .gp-card .pr-label { font-size:12px; font-weight:600; color:#475569; }
        .gp-card .pr-input, .gp-card .pr-select {
          padding:9px 12px; border-radius:10px; border:1px solid #e2e8f0;
          font-size:13px; color:#1e293b; background:white; outline:none;
          transition:border-color .15s, box-shadow .15s;
        }
        .gp-card .pr-input:focus, .gp-card .pr-select:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.12); }
        .gp-card .pr-btn { display:inline-flex; align-items:center; gap:6px; border-radius:10px; font-size:13px; font-weight:600; cursor:pointer; padding:9px 16px; border:none; }
        .gp-card .pr-btn--primary { background:linear-gradient(135deg,#1e3a8a,#3b82f6); color:white; box-shadow:0 3px 8px rgba(59,130,246,.25); }
        .gp-card .pr-btn--primary:disabled { opacity:.6; cursor:not-allowed; }
        .gp-card .pr-btn--ghost { background:rgba(59,130,246,.08); color:#1e40af; border:1px solid rgba(59,130,246,.2); }
        @keyframes pr-spin { to { transform:rotate(360deg); } }
        @keyframes pr-fadeIn { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <div
        className="gp-card"
        style={{
          background: 'white', borderRadius: 16, border: '1px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0,0,0,.05)', padding: 20,
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
        <GeneratePayrollPanel
          variant="bare"
          toast={toast}
          onGenerated={onGenerated}
          prefillEmployee={prefillEmployee}
        />
      </div>
    </Dialog>
  );
};

export default GeneratePayrollModal;